import { useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost, apiPut } from "../../lib/api";

type Branch = { id: string; name: string };
type Service = {
  id: string;
  branch_id: string;
  name: string;
  duration_minutes: number;
  price_cents: number;
  is_active: boolean;
};

type ServiceForm = {
  branch_id: string;
  name: string;
  duration_minutes: number;
  price_cents: number;
  is_active: boolean;
};

function emptyForm(branchId = ""): ServiceForm {
  return { branch_id: branchId, name: "", duration_minutes: 30, price_cents: 2500, is_active: true };
}

export function TgServicesPage() {
  const qc = useQueryClient();
  const [branchFilter, setBranchFilter] = useState("");
  const [createForm, setCreateForm] = useState<ServiceForm>(emptyForm());
  const [editingId, setEditingId] = useState("");
  const [editForm, setEditForm] = useState<ServiceForm>(emptyForm());
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const branches = useQuery<{ items: Branch[] }>({
    queryKey: ["tg-services-branches"],
    queryFn: () => apiGet("/v3/branches?size=100"),
    staleTime: 60_000,
  });

  const services = useQuery<{ items: Service[] }>({
    queryKey: ["tg-services-list"],
    queryFn: () => apiGet("/v3/services?size=200"),
  });

  const branchNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const b of branches.data?.items ?? []) map.set(b.id, b.name);
    return map;
  }, [branches.data?.items]);

  const filtered = useMemo(
    () => (services.data?.items ?? []).filter((s) => !branchFilter || s.branch_id === branchFilter),
    [services.data?.items, branchFilter]
  );

  const createMutation = useMutation({
    mutationFn: (payload: ServiceForm) => apiPost("/v3/services", payload),
    onSuccess: async () => {
      setNotice("Service created");
      setError("");
      setCreateForm((prev) => emptyForm(prev.branch_id));
      await qc.invalidateQueries({ queryKey: ["tg-services-list"] });
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "Create failed");
      setNotice("");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ServiceForm }) => apiPut(`/v3/services/${id}`, payload),
    onSuccess: async () => {
      setNotice("Service updated");
      setError("");
      setEditingId("");
      await qc.invalidateQueries({ queryKey: ["tg-services-list"] });
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "Update failed");
      setNotice("");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/v3/services/${id}`),
    onSuccess: async () => {
      setNotice("Service deleted");
      setError("");
      if (editingId) setEditingId("");
      await qc.invalidateQueries({ queryKey: ["tg-services-list"] });
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "Delete failed");
      setNotice("");
    },
  });

  const beginEdit = (svc: Service) => {
    setEditingId(svc.id);
    setEditForm({
      branch_id: svc.branch_id,
      name: svc.name,
      duration_minutes: svc.duration_minutes,
      price_cents: svc.price_cents,
      is_active: svc.is_active,
    });
  };

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-lg font-bold text-gray-900">Services</h1>
        <span className="text-xs text-gray-500">{filtered.length} shown</span>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-3">
        <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Branch filter</label>
        <select
          className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          value={branchFilter}
          onChange={(e) => setBranchFilter(e.target.value)}
        >
          <option value="">All branches</option>
          {(branches.data?.items ?? []).map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-3 space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Quick add</p>
        <ServiceFormFields
          form={createForm}
          branches={branches.data?.items ?? []}
          onChange={setCreateForm}
          compact
        />
        <button
          className="w-full rounded-lg bg-teal-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
          disabled={!createForm.branch_id || !createForm.name.trim() || createMutation.isPending}
          onClick={() => createMutation.mutate(createForm)}
        >
          {createMutation.isPending ? "Creating..." : "Create service"}
        </button>
      </div>

      {notice && <div className="rounded-lg bg-emerald-50 p-2 text-sm text-emerald-700">{notice}</div>}
      {error && <div className="rounded-lg bg-rose-50 p-2 text-sm text-rose-700">{error}</div>}

      {services.isLoading && <div className="text-center text-sm text-gray-500">Loading services...</div>}

      <div className="space-y-2">
        {filtered.map((svc) => {
          const isEditing = editingId === svc.id;
          return (
            <div key={svc.id} className="rounded-xl border border-gray-200 bg-white p-3">
              {isEditing ? (
                <div className="space-y-2">
                  <ServiceFormFields form={editForm} branches={branches.data?.items ?? []} onChange={setEditForm} compact />
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={editForm.is_active}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, is_active: e.target.checked }))}
                    />
                    Active
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      className="rounded-lg bg-teal-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
                      disabled={!editForm.branch_id || !editForm.name.trim() || updateMutation.isPending}
                      onClick={() => updateMutation.mutate({ id: svc.id, payload: editForm })}
                    >
                      Save
                    </button>
                    <button
                      className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700"
                      onClick={() => setEditingId("")}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{svc.name}</p>
                      <p className="text-xs text-gray-500">
                        {branchNameMap.get(svc.branch_id) || "Unknown branch"} · {svc.duration_minutes} min · $
                        {(svc.price_cents / 100).toFixed(2)}
                      </p>
                    </div>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                        svc.is_active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {svc.is_active ? "active" : "inactive"}
                    </span>
                  </div>
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    <button
                      className="rounded-lg border border-gray-300 px-2 py-1.5 text-xs font-semibold text-gray-700"
                      onClick={() => beginEdit(svc)}
                    >
                      Edit
                    </button>
                    <button
                      className="rounded-lg border border-teal-200 bg-teal-50 px-2 py-1.5 text-xs font-semibold text-teal-700"
                      onClick={() =>
                        setCreateForm({
                          branch_id: svc.branch_id,
                          name: `${svc.name} (copy)`,
                          duration_minutes: svc.duration_minutes,
                          price_cents: svc.price_cents,
                          is_active: true,
                        })
                      }
                    >
                      Clone
                    </button>
                    <button
                      className="rounded-lg border border-rose-200 bg-rose-50 px-2 py-1.5 text-xs font-semibold text-rose-700"
                      onClick={() => deleteMutation.mutate(svc.id)}
                      disabled={deleteMutation.isPending}
                    >
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          );
        })}
        {!services.isLoading && filtered.length === 0 && (
          <div className="rounded-xl border border-dashed border-gray-300 p-5 text-center text-sm text-gray-500">
            No services for current filter.
          </div>
        )}
      </div>
    </div>
  );
}

function ServiceFormFields({
  form,
  branches,
  onChange,
  compact = false,
}: {
  form: ServiceForm;
  branches: Branch[];
  onChange: Dispatch<SetStateAction<ServiceForm>>;
  compact?: boolean;
}) {
  const current = form;
  return (
    <div className={`grid gap-2 ${compact ? "" : "mt-2"}`}>
      <select
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
        value={current.branch_id}
        onChange={(e) => onChange((prev) => ({ ...prev, branch_id: e.target.value }))}
      >
        <option value="">Select branch</option>
        {branches.map((b) => (
          <option key={b.id} value={b.id}>
            {b.name}
          </option>
        ))}
      </select>
      <input
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
        value={current.name}
        placeholder="Service name"
        onChange={(e) => onChange((prev) => ({ ...prev, name: e.target.value }))}
      />
      <div className="grid grid-cols-2 gap-2">
        <input
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          type="number"
          min={15}
          step={15}
          value={current.duration_minutes}
          onChange={(e) => onChange((prev) => ({ ...prev, duration_minutes: Number(e.target.value || "0") }))}
        />
        <input
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          type="number"
          min={0}
          step={100}
          value={current.price_cents}
          onChange={(e) => onChange((prev) => ({ ...prev, price_cents: Number(e.target.value || "0") }))}
        />
      </div>
    </div>
  );
}
